/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useAnimatedNumber } from './useAnimatedNumber';
import { Category } from '../data';

interface CategoryCardProps {
    category: Category;
    fingerCount?: number;
    matchCount?: number;
    animationDelay?: number;
    animate?: boolean;
    isPlaceholder?: boolean;
}

export function CategoryCard({ category, fingerCount = 0, matchCount = 0, animationDelay = 0, animate = true, isPlaceholder = false }: CategoryCardProps) {
    const theme: any = useTheme();

    // Card animation duration in ms
    const cardAnimDuration = 200;

    // Numbers start animating after card animation completes
    const numberAnimDelay = animate ? animationDelay + cardAnimDuration : 0;

    const animatedFingerCount = useAnimatedNumber(fingerCount, { delay: numberAnimDelay, enabled: animate && !isPlaceholder });
    const animatedMatchCount = useAnimatedNumber(matchCount, { delay: numberAnimDelay, enabled: animate && !isPlaceholder });

    const cardStyle = {
        backgroundColor: theme.colors.dark,
        borderColor: theme.colors.grey,
        color: theme.colors.text,
        animation: animate ? `dropCard ${cardAnimDuration}ms ease-out ${animationDelay}ms both` : undefined,
        opacity: isPlaceholder ? 0 : undefined,
        visibility: isPlaceholder ? ('hidden' as const) : undefined,
        cursor: isPlaceholder ? undefined : 'pointer',
        textDecoration: 'none',
    };
    const headerStyle = {
        backgroundColor: theme.colors.headerBg,
        borderColor: theme.colors.grey,
        color: theme.colors.text,
        padding: '0.75rem 1rem',
        margin: 0,
        minHeight: '4rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
    const textStyle = { color: theme.colors.textMuted };
    const cardBodyStyle = {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'space-between',
        height: '100%',
    };
    const keyStyle = {
        color: theme.colors.ctext,
        fontFamily: 'monospace',
        fontSize: '0.9rem',
        marginBottom: '0.5rem',
    };

    return (
        <>
            <style>{`
                @keyframes dropCard {
                    0% {
                        opacity: 0;
                        transform: translateY(-20px) scale(1.15);
                    }
                    60% {
                        opacity: 1;
                        transform: translateY(0) scale(1.05);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .category-card-link .card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .category-card-link:hover .card {
                    transform: translateY(-4px);
                    box-shadow: 0 4px 12px rgba(0, 255, 255, 0.2);
                }
            `}</style>
            <Col key={category.key} xs={12} sm={6} lg={3}>
                <Link to={`/categories/${category.key}`} style={{ textDecoration: 'none', color: 'inherit' }} className="category-card-link">
                    <Card className="h-100" style={cardStyle}>
                    <Card.Header as="h5" style={headerStyle}>{category.title}</Card.Header>
                    <Card.Body style={cardBodyStyle}>
                        <div className="d-flex justify-content-between align-items-start">
                            <div style={keyStyle}>
                                {category.key}
                            </div>
                            <div style={{ textAlign: 'right', color: theme.colors.textMuted, fontSize: '0.85rem' }}>
                                {animatedFingerCount} Applications
                            </div>
                        </div>
                        <div>
                            <Card.Text style={{ ...textStyle, textAlign: 'center', marginBottom: '0.25rem' }}>
                                {category.description}
                            </Card.Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline' }}>
                            <div style={{ width: '50%', textAlign: 'right', paddingRight: '0.5rem' }}>
                                <div className="digital-number" style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.ctext }}>{animatedMatchCount}</div>
                            </div>
                            <div style={{ width: '50%', textAlign: 'left', paddingLeft: '0.5rem' }}>
                                <div style={{ fontSize: '1rem' }}>Risk Routes</div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                </Link>
            </Col>
        </>
    );
}
